import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { personalApiKeyService } from '~/services/userAPI';
import {
  ApiQueryFnData,
  formatErrorPayload,
  UseApiMutationOptions,
  UseApiQueryOptions,
  validateApiResponse,
} from '../utils';

const ROOT_KEY = 'apiKeys';
const getRootQueryKey = () => [ROOT_KEY];

export const useApiKeysQuery = (
  options: UseApiQueryOptions<typeof personalApiKeyService.getApiKeys> = {},
) => {
  const dispatch = useDispatch();

  return useQuery(
    getRootQueryKey(),
    async () => validateApiResponse(await personalApiKeyService.getApiKeys()).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useApiKeyMutation = <T extends typeof personalApiKeyService.getApiKeyById>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();

  return useMutation(
    async (data: ApiQueryFnData<T>['id']) =>
      validateApiResponse(await personalApiKeyService.getApiKeyById(data)).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useCreateApiKeyMutation = <T extends typeof personalApiKeyService.createApiKey>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async (data: ApiQueryFnData<T>['name']) =>
      validateApiResponse(await personalApiKeyService.createApiKey(data)).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      onSuccess: () => queryClient.invalidateQueries(getRootQueryKey()),
      ...options,
    },
  );
};

export const useEditApiKeyMutation = <T extends typeof personalApiKeyService.editApiKey>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: ApiQueryFnData<T>['id']; data: Partial<ApiQueryFnData<T>> }) =>
      validateApiResponse(await personalApiKeyService.editApiKey(id, data)).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      onSuccess: () => queryClient.invalidateQueries(getRootQueryKey()),
      ...options,
    },
  );
};

export const useDeleteApiKeysMutation = <T extends typeof personalApiKeyService.deleteApiKeyById>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async (data: ApiQueryFnData<T>['id'][]) => {
      const responses = await Promise.all(
        data.map((item) => personalApiKeyService.deleteApiKeyById(item)),
      );
      return responses.map((response) => validateApiResponse(response).data);
    },
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      onSuccess: () => queryClient.invalidateQueries(getRootQueryKey()),
      ...options,
    },
  );
};
